import { CoreApplicationContext } from '@eventbrite/context-gen';
import { DestinationEvent } from '@eventbrite/event-renderer';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { FeatureFlags } from '@eventbrite/feature-flag-utils';
import {
    CORE_SEO_API_KEY_CONFIG,
    useFeatureFlagState,
} from '@eventbrite/splitio-checker';
import loadable from '@loadable/component';
import React, { useState } from 'react';
import { CategoryFilterBar } from '../../components/CategoryFilterBar';
import { categoryToIconMap } from '../../components/CategoryFilterBar/categoryToIconMap';
import {
    useGetSelectedCategory,
    useReactQueryEnabler,
    useSingleToggle,
} from '../../components/CategoryFilterBar/hooks';
import { DateOption, DATE_OPTIONS } from '../../components/DateFilterDropdown';
import { LazyRenderer } from '../../components/LazyRenderer';
import { Organizer } from '../../components/OrganizersSection/utils';
import { HeroSection, HeroSectionProps } from '../../containers/HeroSection';
import { NearbyCitiesProps } from '../../containers/NearbyCities';
import {
    Breadcrumb,
    ThingsToDoBreadcrumbs,
} from '../../containers/ThingsToDoBreadcrumbs';
import {
    TopActivities,
    TopActivitiesProps,
} from '../../containers/TopActivities';
import { TrendingSearchesProps } from '../../containers/TrendingSearches';
import { CollectionShape } from '../../containers/UniqueExperiences';
import { Category } from '../../containers/WaysToTour';
import { useEnvContext } from '../../context';
import { useQuerySearchService } from '../../hooks/eventHooks';
import { LocationData } from '../../types/AppProps';
import { userOperatingSystemIs } from '../../utils';
import {
    BentoCarouselSection,
    ORDERED_TTD_CATEGORIES,
} from '../point-of-interest/containers/BentoCarouselSection';
import { CategoryCarousel } from '../point-of-interest/containers/CategoryCarousel';
import styles from './ThingsToDoPage.module.scss';

const WaysToTour = loadable(() => import('../../containers/WaysToTour'), {
    resolveComponent: (components: { WaysToTour: React.FunctionComponent }) =>
        components.WaysToTour,
});

const UniqueExperiences = loadable(
    () => import('../../containers/UniqueExperiences'),
    {
        resolveComponent: (components: {
            UniqueExperiences: React.FunctionComponent;
        }) => components.UniqueExperiences,
    },
);

const TopOrganizers = loadable(() => import('../../containers/TopOrganizers'), {
    resolveComponent: (components: {
        TopOrganizers: React.FunctionComponent;
    }) => components.TopOrganizers,
});

const TrendingSearches = loadable(
    () => import('../../containers/TrendingSearches'),
    {
        resolveComponent: (components: {
            TrendingSearches: React.FunctionComponent;
        }) => components.TrendingSearches,
    },
);

const ThingsToDoSection = loadable(
    () => import('../../components/ThingsToDoSection'),
    {
        resolveComponent: (components: {
            ThingsToDoSection: React.FunctionComponent;
        }) => components.ThingsToDoSection,
    },
);

const NearbyPOI = loadable(
    () => import('../../pages/point-of-interest/containers/NearbyPOI'),
    {
        resolveComponent: (components: {
            NearbyPOI: React.FunctionComponent;
        }) => components.NearbyPOI,
    },
);

const NearbyCities = loadable(() => import('../../containers/NearbyCities'), {
    resolveComponent: (components: { NearbyCities: React.FunctionComponent }) =>
        components.NearbyCities,
});

const FAQSection = loadable(() => import('../../containers/FAQSection'), {
    resolveComponent: (components: { FAQSection: React.FunctionComponent }) =>
        components.FAQSection,
});

const BrowseByDate = loadable(() => import('../../components/BrowseByDate'), {
    resolveComponent: (components: { BrowseByDate: React.FunctionComponent }) =>
        components.BrowseByDate,
});

interface ThingsToDoProps {
    env: CoreApplicationContext['env'];
    breadcrumbs: Breadcrumb[];
    trendingSearchesResult: TrendingSearchesProps;
    topEventsData: TopActivitiesProps;
    nearbyEvents?: Array<DestinationEvent>;
    nearbyFreeEvents?: Array<DestinationEvent>;
    nearbyNightlifeEvents?: Array<DestinationEvent>;
    nearbyCitiesResult?: NearbyCitiesProps;
    topOrganizers: Organizer[];
    categories?: Category[];
    heroSection: HeroSectionProps;
    editorialCollections: CollectionShape[];
    featureFlags: FeatureFlags;
    query_location_data: LocationData;
    userId: string | undefined;
    place_id: string;
    location_slug: string;
    language_code: string;
    point_of_interest_shelf?: PointOfInterestShelfState;
    locale?: string;
    languageSubdirectory?: string;
}
export interface PointOfInterestShelfState {
    location: string;
    tags: PointOfInterestShelfTag[];
}

export interface PointOfInterestShelfTag {
    pointOfInterestName: string;
    linkUrl: string;
}

export const ThingsToDoPage: React.FunctionComponent<ThingsToDoProps> = (
    props,
) => {
    const IS_IOS_USER = userOperatingSystemIs('iOS');
    const enabledPOINearLocationForTTD = useFeatureFlagState(
        'enable_poi_near_location_for_ttd',
        CORE_SEO_API_KEY_CONFIG,
    );
    const enableTTDBentoCarouselExperience = useFeatureFlagState(
        'enable_ttd_bento_carousel_experience',
        CORE_SEO_API_KEY_CONFIG,
    );
    const enabledTTDCategoryFilter =
        useFeatureFlagState(
            'enable_ttd_category_filter',
            CORE_SEO_API_KEY_CONFIG,
            { userId: props.userId ?? '' },
        ) && !IS_IOS_USER;
    const enabledTTDDateFilter =
        useFeatureFlagState('enable_ttd_date_filter', CORE_SEO_API_KEY_CONFIG, {
            userId: props.userId ?? '',
        }) && !IS_IOS_USER;

    const { shouldEnableQuery, enableQuery } = useReactQueryEnabler();
    const { setCategoryTag } = useGetSelectedCategory();
    const { handleToggled, selectedCategory, resetFiltersRef } =
        useSingleToggle({
            categories: categoryToIconMap,
        });
    const [numCarousels, setNumCarousels] = useState(5);
    const [dateSelection, changeDateSelection] = useState<DateOption>(
        DATE_OPTIONS[0],
    );

    const getIsOnline = () => {
        if (HAS_WINDOW) {
            return window.location.href.includes('online');
        }
        return false;
    };
    const envContext = useEnvContext();
    const isBotRequest = envContext?.isBotRequest;

    const { data, isLoading } = useQuerySearchService({
        queryOverload: {
            page_size: 100,
            include_manual_events: true,
            include_mature_events: true,
            dates: [dateSelection.searchValue],
            page_type: 'TTD',
            tags: [selectedCategory?.tagSelector ?? ''],
            online_events_only: getIsOnline(),
        },
        location: {
            latitude: Number(props.query_location_data.latitude),
            longitude: Number(props.query_location_data.longitude),
            isOnline: props.query_location_data.isOnline ?? getIsOnline(),
            placeId: props.place_id,
        },
        enabled: shouldEnableQuery,
        retryOnMount: false,
    });
    const isTTDFilterAndQueryEnabled =
        enabledTTDCategoryFilter && shouldEnableQuery;

    const renderTopActivitiesAndCategoryCarousel = () => (
        <>
            <TopActivities
                topActivitiesResult={{
                    top_events: data?.topActivities?.top_events ?? [],
                    num_online_events:
                        data?.topActivities?.num_online_events ?? 0,
                    num_physical_events:
                        data?.topActivities?.num_physical_events ?? 0,
                    shareProps: data?.topActivities?.shareProps,
                    isLoading: isLoading,
                }}
                languageSubdirectory={props.languageSubdirectory}
            />
            <CategoryCarousel
                category_events={data?.category_events}
                isLoading={isLoading}
                populatedCategoriesCount={data?.carouselCount || numCarousels}
                setNumCarousels={setNumCarousels}
                locationData={{
                    latitude: Number(props.query_location_data.latitude),
                    longitude: Number(props.query_location_data.longitude),
                    isOnline:
                        props.query_location_data.isOnline ?? getIsOnline(),
                }}
                pageType="TTD"
                handleToggle={handleToggled}
                changeDateSelection={changeDateSelection}
            />
        </>
    );

    const renderTopActivitiesAndBentoCarousel = () => (
        <>
            <TopActivities
                topActivitiesResult={props.topEventsData}
                languageSubdirectory={props.languageSubdirectory}
            />
            <BentoCarouselSection
                orderedCategories={ORDERED_TTD_CATEGORIES}
                nearbyEvents={props.nearbyEvents || []}
                nearbyFreeEvents={props.nearbyFreeEvents || []}
                nearbyNightlifeEvents={props.nearbyNightlifeEvents || []}
                location_slug={props.location_slug}
                languageSubdirectory={props.languageSubdirectory}
            />
        </>
    );

    const renderDefaultTopActivities = () => (
        <TopActivities
            topActivitiesResult={props.topEventsData}
            languageSubdirectory={props.languageSubdirectory}
        />
    );

    const RenderEvents = () => {
        if (isTTDFilterAndQueryEnabled) {
            return renderTopActivitiesAndCategoryCarousel();
        } else if (enableTTDBentoCarouselExperience) {
            return renderTopActivitiesAndBentoCarousel();
        }
        return renderDefaultTopActivities();
    };

    return (
        <>
            <ThingsToDoBreadcrumbs breadcrumbs={props.breadcrumbs} />
            <HeroSection
                placeImage={props.heroSection.placeImage}
                isPageArt={props.heroSection.isPageArt}
                artistName={props.heroSection.artistName}
                artistInstagramLink={props.heroSection.artistInstagramLink}
            />
            {enabledTTDCategoryFilter ? (
                <CategoryFilterBar
                    categories={categoryToIconMap}
                    pageType="TTD"
                    locationData={{
                        latitude: Number(
                            props.query_location_data?.latitude ?? 0,
                        ),
                        longitude: Number(
                            props.query_location_data.longitude ?? 0,
                        ),
                        isOnline: props.query_location_data?.isOnline ?? false,
                    }}
                    enableQuery={enableQuery}
                    dateSelection={dateSelection}
                    changeDateSelection={changeDateSelection}
                    setCategoryTag={setCategoryTag}
                    handleToggled={handleToggled}
                    selectedCategory={selectedCategory}
                    enableDateFilter={enabledTTDDateFilter}
                    ref={resetFiltersRef}
                />
            ) : null}
            <LazyRenderer numChildInitialRender={isBotRequest ? -1 : 2}>
                <RenderEvents />
                <WaysToTour categories={props.categories} />
                <UniqueExperiences
                    editorialCollections={props.editorialCollections}
                />
                {props.topOrganizers?.length ? (
                    <TopOrganizers topOrganizers={props.topOrganizers} />
                ) : (
                    <></>
                )}

                <TrendingSearches
                    languageSubdirectory={props.languageSubdirectory}
                    trendingSearchesResult={props.trendingSearchesResult}
                />
                {enabledPOINearLocationForTTD && (
                    <NearbyPOI
                        pointOfInterestName={
                            props.point_of_interest_shelf?.location
                        }
                        nearbyPointsOfInterest={
                            props.point_of_interest_shelf?.tags || []
                        }
                        className={styles.cityBrowsePoiShelf}
                    />
                )}

                {props.nearbyCitiesResult ? (
                    <ThingsToDoSection>
                        <NearbyCities
                            places={props.nearbyCitiesResult.places}
                        />
                    </ThingsToDoSection>
                ) : (
                    <></>
                )}
                <FAQSection
                    topEventsBucket={props.topEventsData.top_events || []}
                    topOrganizersBucket={props?.topOrganizers || []}
                    nearbyCitiesBucket={props.nearbyCitiesResult?.places || []}
                    trendingSearchesBucket={
                        props.trendingSearchesResult.trending || []
                    }
                />
                <BrowseByDate
                    location_slug={props.location_slug}
                    language_code={props.language_code}
                    language_subdirectory={props.languageSubdirectory}
                />
            </LazyRenderer>
        </>
    );
};
